import {PTPStepsService} from "../services/ptpsteps.service";
import {inject, Optional} from "@angular/core";
import Utils from "src/utils";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PerFormaAPIService} from "src/app/services/pfapi.service";
import {FileDownloadService} from "src/app/services/file-download.service";
import {Observable} from "rxjs";

// used for some api responses
export interface PTPResponse {
    messages: string[];
    errors: string[];
}

export default abstract class BasePtpForm {
    protected Loading = true;
    protected StepService: PTPStepsService = inject(PTPStepsService);
    protected fileDownload: FileDownloadService = inject(FileDownloadService);
    protected Steps: any = null;

    protected DoScroll = false;
    protected ScrollTo = "";

    DenyRequest = {
        DenyNotes: "",
        DenyID: 0,
        DenyType: 0
    };

    ModalOptions = {animation: false, backdropClass: "backdrop"};
    ModalFeatures = "height=700,width=1110,scrollbars=yes,resizable=yes,menubar=no";

    constructor(
        protected api: PerFormaAPIService,
        @Optional() protected modal: NgbModal = null
    ) {
        this.GetSteps();
    }

    // load the PTP Steps available to the current login
    GetSteps = async () => {
        this.Steps = await this.StepService.Steps();
    };

    // download a purchase-request attachment via the authed client (a plain <a href> would 401)
    OpenAttachment = (attachmentId: number) => {
        this.fileDownload.openPtpAttachment(attachmentId);
    };

    Success = message => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        window.parent.successAlert(message);
    };

    // show a warning message
    Error = message => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        window.parent.errorAlert(message);
    };

    abstract Refresh();

    ScrollToElement = elementId => {
        // unset the variables so we only do this once
        this.DoScroll = false;
        this.ScrollTo = "";

        document.getElementById(elementId).scrollIntoView({behavior: "smooth", block: "start"});
    };

    // closes the page modal
    CloseModal = () => this.modal.dismissAll();

    ApproveItem = (item, type?, id?) => {
        this.Loading = true;

        // grab the current data to make sure we have values
        const data$: Observable<any> =
            item.ItemType === 0
                ? this.api.getApiPTPGetPORequest({poRequestId: item.ItemID})
                : this.api.getApiPTPGetPO({poId: item.ItemID});

        data$.subscribe((data: any) => {
            let errors = false;
            const location = data.Location;

            if (this.RequiresVendorNameAndNumber(data)) {
                const vendorName = data.VendorName.trim().toLowerCase();
                const vendorNumber = data.VendorNumber.trim();

                if (vendorName === "" || vendorName === "unspecified") {
                    this.Error("Vendor Name is Required");
                    errors = true;
                }

                if (vendorNumber === "") {
                    this.Error("Vendor Number is Required");
                    errors = true;
                }
            }

            if (location === undefined || location === 0) {
                this.Error("Location is Required");
                errors = true;
            }

            if (errors) {
                this.Error("Please Save before approving");
                this.Loading = false;
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const outerThis = this;

            const request = {
                ItemIDs: [data.ItemID ?? data.POID],
                Reference: String(data.ItemType ?? 1)
            };

            this.api.postApiPTPApprove(request).subscribe({
                next: data => {
                    data.Messages.forEach(m => this.Success(m));
                    data.Errors.forEach(m => this.Error(m));
                    outerThis.Refresh();
                },
                error: error => {
                    outerThis.Error(error.error.Message);
                    outerThis.Loading = false;
                }
            });
        });
    };

    // called when a single item is denied
    DenyItem = (item, template) => {
        this.DenyRequest.DenyNotes = "";
        this.DenyRequest.DenyID = item.ItemID;
        this.DenyRequest.DenyType = item.ItemType;
        this.modal.open(template, this.ModalOptions);
    };

    DoDenyItem = () => {
        this.CloseModal();
        this.Loading = true;

        const request = {
            ItemIDs: [this.DenyRequest.DenyID],
            Reference: String(this.DenyRequest.DenyType),
            Reference2: this.DenyRequest.DenyNotes
        };

        this.api.postApiPTPDeny(request).subscribe({
            next: data => {
                data.Messages.forEach(m => this.Success(m));

                this.DenyRequest.DenyNotes = "";
                this.DenyRequest.DenyID = 0;
                this.DenyRequest.DenyType = 0;

                this.Refresh();
            },
            error: error => {
                this.Error(error.error.Message);
                this.Loading = false;
            }
        });
    };

    // returns true if the current login has access to the given step id and it is the "Request" step
    IsFirstStep = id => {
        if (!id || id === 0) return true;
        return this.Steps.filter(s => s.StepID === id && (s.StepName === "Request" || s.StepName === "PO")).length > 0;
    };

    // returns true if the current login has access to the given step id
    HasStepPermission = id => {
        if (!id || id === 0) return true;
        return this.Steps.filter(s => s.StepID === id).length > 0;
    };

    BuildNotesTable = item => {
        let notes = "No notes.";

        if (item.Notes?.length > 0) {
            notes =
                '<table class="table table-striped" style="margin-bottom:0;"><thead><tr><th>Time</th><th>Author</th><th>Note</th></tr></thead>';

            (item.Notes as any[]).forEach(n => {
                notes += `<tr><td>${Utils.FormatDate(new Date(n.NoteTime + "Z"))}</td><td>${n.UserName}</td><td>${n.Note}</td></tr>`;
            });

            notes += "</table>";
        }

        return notes;
    };

    BuildTransactionsTable = item => {
        let transactions = "No history.";

        if (item.Transactions?.length > 0) {
            transactions =
                '<table class="table table-striped" style="margin-bottom:0;"><thead><tr><th>Time</th><th>Transaction</th></tr></thead>';

            (item.Transactions as any[]).forEach(h => {
                transactions += `<tr><td>${Utils.FormatDate(new Date(h.HistoryTime + "Z"))}</td><td>${h.Transaction}</td></tr>`;
            });

            transactions += "</table>";
        }

        return transactions;
    };

    // check if the Vendor Name and Vendor Number fields are required
    RequiresVendorNameAndNumber = item => {
        // prevent anything other than PO/PRs from being checked
        if (item.ItemType === undefined || item.ItemType === null) {
            return false;
        }

        // POs always require these fields
        if (item.ItemType === 1) {
            return true;
        }

        // it's a purchase request
        if (item.ItemType === 0) {
            // find the "Source Determination" step
            const sourceStep = this.Steps.filter(s => s.StepType === 0 && s.StepName === "Source Determination").pop();

            // couldn't find it
            if (sourceStep === undefined) {
                return false;
            }

            // get the ids of all purchase request steps that are "Source Determination" or after it
            const greaterStepIDs = this.Steps.filter(s => s.StepType === 0 && s.Order >= sourceStep.Order).map(
                s => s.StepID
            );

            // check if the current step is in the list of those
            // that require the fields
            if (greaterStepIDs.indexOf(item.StepID) >= 0) {
                return true;
            }
        }

        return false;
    };
}
